import type { ComponentType } from 'react';
import type { RouteObject } from 'react-router';
import type { Permission } from '#shared/types';
import { RequireCapability } from './RequireCapability';

/**
 * Routes that render only for someone holding `capability` — or, given a
 * list, any one of them. Presentation only: the server enforces the same.
 */
export function gate(
  capability: Permission | readonly Permission[],
  children: RouteObject[],
): RouteObject {
  return { element: <RequireCapability capability={capability} />, children };
}

type CataloguePermission =
  | 'labTest'
  | 'testParameter'
  | 'procedure'
  | 'reportComment'
  | 'reportRemark'
  | 'antibiotic'
  | 'organism'
  | 'department'
  | 'consultant';

interface CatalogueRoutes {
  /** The permission prefix: `labTest` gates on `labTest.view`, `.create`, `.edit`. */
  permission: CataloguePermission;
  path: string;
  List: ComponentType;
  Detail: ComponentType;
  Form: ComponentType;
}

/**
 * The routes a catalogue list has — list, detail, new, edit — each gated on
 * its own permission, so "may read lab tests but not add one" is a role an
 * admin can build rather than a code change.
 */
export function catalogueRoutes({
  permission,
  path,
  List,
  Detail,
  Form,
}: CatalogueRoutes): RouteObject[] {
  return [
    gate(`${permission}.view`, [
      { path, element: <List /> },
      { path: `${path}/:id`, element: <Detail /> },
    ]),
    // Static before dynamic, as with `patients/new`.
    gate(`${permission}.create`, [{ path: `${path}/new`, element: <Form /> }]),
    gate(`${permission}.edit`, [{ path: `${path}/:id/edit`, element: <Form /> }]),
  ];
}
